(function () {
  'use strict';

  const tag = 'woolie-spark-deferred';
  if (!window.customElements || customElements.get(tag)) return;

  const hosts = new Set();
  const prepared = new WeakSet();
  const activated = new WeakSet();
  let started = false;
  let phase = 'idle';
  let observer;
  let resizeObserver;
  let placeholderTimer;
  const pendingPlaceholders = new Map();
  const rootOwners = new Map();

  const scriptType = (script) => (script.getAttribute('type') || '').trim().toLowerCase();
  const isClassic = (script) => /^(|text\/(java|ecma)script|application\/(java|ecma)script)$/.test(scriptType(script));
  const isData = (script) => /^(application\/(ld\+)?json|text\/json)$/.test(scriptType(script));

  function releasePlaceholder(host, remove) {
    for (const root of pendingPlaceholders.get(host) || []) {
      if (resizeObserver) resizeObserver.unobserve(root);
      rootOwners.delete(root);
    }
    pendingPlaceholders.delete(host);
    if (remove) {
      const placeholder = host.querySelector('[data-spark-placeholder]');
      if (placeholder) placeholder.remove();
    }
    if (!pendingPlaceholders.size) {
      if (resizeObserver) resizeObserver.disconnect();
      if (placeholderTimer !== undefined) clearTimeout(placeholderTimer);
      placeholderTimer = undefined;
    }
  }

  function watchPlaceholder(host) {
    const roots = Array.from(host.querySelectorAll('.spark-carousal-root'));
    pendingPlaceholders.set(host, roots);
    for (const root of roots) {
      rootOwners.set(root, host);
      if (resizeObserver) resizeObserver.observe(root);
    }
  }

  function fail(host) {
    releasePlaceholder(host, false);
    if (!host.isConnected) return;
    host.dataset.sparkState = 'error';
    host.removeAttribute('aria-busy');
    let placeholder = host.querySelector('[data-spark-placeholder]');
    if (!placeholder) {
      placeholder = document.createElement('div');
      placeholder.setAttribute('data-spark-placeholder', '');
      host.append(placeholder);
    }
    const message = document.createElement('p');
    message.textContent = 'Não foi possível carregar os vídeos.';
    const reload = document.createElement('a');
    reload.href = window.location.href;
    reload.textContent = 'Recarregar a página';
    placeholder.replaceChildren(message, reload);
  }

  function copyScript(source) {
    const script = document.createElement('script');
    for (const attribute of source.attributes) script.setAttribute(attribute.name, attribute.value);
    if (source.nonce) script.nonce = source.nonce;
    script.textContent = source.textContent;
    return script;
  }

  function insertScript(record) {
    const script = copyScript(record.source);
    const waitsForLoad = script.hasAttribute('src') || scriptType(script) === 'module';
    const result = waitsForLoad ? new Promise((resolve, reject) => {
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
    }) : Promise.resolve();
    record.marker.replaceWith(script);
    return result;
  }

  async function activate() {
    if (!started || phase !== 'idle') return;
    const group = Array.from(hosts).filter((host) => host.isConnected && !activated.has(host));
    if (!group.length) return;
    phase = 'loading';
    if (observer) observer.disconnect();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const host = rootOwners.get(entry.target);
          if (host && (!host.isConnected || entry.contentRect.height > 80)) releasePlaceholder(host, true);
        }
      });
    }
    const records = [];
    try {
      // Every root must exist before any vendor configuration emits its ready event.
      for (const host of group) {
        const template = host.querySelector('template[data-spark-content]');
        if (!template) throw new Error('Missing Spark content');
        const fragment = template.content.cloneNode(true);
        for (const script of fragment.querySelectorAll('script')) {
          if (scriptType(script) === 'application/ld+json') {
            // Liquid already emits the original structured data outside the template.
            script.remove();
          } else if (!isData(script)) {
            const marker = document.createComment('Spark script');
            script.replaceWith(marker);
            records.push({ source: script, marker });
          }
        }
        activated.add(host);
        host.dataset.sparkState = 'loading';
        host.setAttribute('aria-busy', 'true');
        template.replaceWith(fragment);
        watchPlaceholder(host);
      }

      // The current vendor contract is inline classic configuration + one module.
      // Future classic external scripts fall back to immediate, ordered loading.
      for (const record of records.filter((item) => isClassic(item.source))) {
        await insertScript(record);
      }
      const seenModules = new Set();
      const modules = [];
      for (const record of records.filter((item) => !isClassic(item.source))) {
        if (scriptType(record.source) === 'module' && record.source.hasAttribute('src')) {
          const key = new URL(record.source.getAttribute('src'), document.baseURI).href;
          if (seenModules.has(key)) {
            record.marker.remove();
            continue;
          }
          seenModules.add(key);
        }
        modules.push(insertScript(record));
      }
      await Promise.all(modules);
      phase = 'loaded';
      for (const host of group) {
        host.dataset.sparkState = 'loaded';
        host.removeAttribute('aria-busy');
      }
      if (pendingPlaceholders.size) {
        // A successful module can still be fetching React chunks and the feed.
        // Empty feeds never grow: release their reservation once, without polling.
        placeholderTimer = setTimeout(() => {
          placeholderTimer = undefined;
          for (const host of Array.from(pendingPlaceholders.keys())) releasePlaceholder(host, true);
        }, 10000);
      }
    } catch (error) {
      phase = 'error';
      group.forEach(fail);
    }
  }

  function hasNewScriptContract(host) {
    const template = host.querySelector('template[data-spark-content]');
    if (!template) return true;
    return Array.from(template.content.querySelectorAll('script')).some((script) => {
      if (isData(script)) return false;
      return !((isClassic(script) && !script.hasAttribute('src')) ||
        (scriptType(script) === 'module' && script.hasAttribute('src')));
    });
  }

  function prepare(host) {
    if (prepared.has(host)) return;
    prepared.add(host);
    host.dataset.sparkState = 'waiting';
    host.addEventListener('focusin', activate);
    const button = host.querySelector('[data-spark-load]');
    if (button) button.addEventListener('click', (event) => {
      event.preventDefault();
      activate();
    });
  }

  function start() {
    started = true;
    for (const host of hosts) prepare(host);
    if (!('IntersectionObserver' in window) || Array.from(hosts).some(hasNewScriptContract)) {
      activate();
      return;
    }
    observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.target.isConnected && hosts.has(entry.target))) {
        activate();
      }
    }, { rootMargin: '600px 0px' });
    for (const host of hosts) observer.observe(host);
  }

  customElements.define(tag, class extends HTMLElement {
    connectedCallback() {
      hosts.add(this);
      if (!started || activated.has(this)) return;
      prepare(this);
      // Late sections cannot safely ask the vendor to remount existing React roots.
      if (phase !== 'idle') {
        fail(this);
      } else if (!observer || hasNewScriptContract(this)) {
        activate();
      } else {
        observer.observe(this);
      }
    }

    disconnectedCallback() {
      hosts.delete(this);
      if (observer) observer.unobserve(this);
      releasePlaceholder(this, activated.has(this));
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
